import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Lock, CheckCircle } from 'lucide-react';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Alert from '../components/common/Alert';
import { authService } from '../services/authService';
import { validateRequired } from '../utils/validators';
import { UserRole } from '../types';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Dependente usa a mesma tela do associado (/associado/*).
  const userType: UserRole = location.pathname.includes('/associado/') ? 'associate' : 'admin';

  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError('Token de recuperação não encontrado. Solicite um novo link de recuperação.');
    }
  }, [searchParams]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!validateRequired(password)) {
      newErrors.password = 'Senha é obrigatória';
    } else if (password.length < 6) {
      newErrors.password = 'Senha deve ter no mínimo 6 caracteres';
    }

    if (!validateRequired(passwordConfirmation)) {
      newErrors.passwordConfirmation = 'Confirmação de senha é obrigatória';
    } else if (password !== passwordConfirmation) {
      newErrors.passwordConfirmation = 'As senhas não conferem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Token de recuperação inválido');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(token, password, passwordConfirmation, userType);
      setSuccess(true);

      // Redirecionar para login após 3 segundos
      setTimeout(() => {
        navigate(userType === 'associate' ? '/associado/login' : '/login');
      }, 3000);
    } catch (err: any) {
      if (err.response?.data?.errors) {
        const serverErrors: Record<string, string> = {};
        Object.keys(err.response.data.errors).forEach(key => {
          serverErrors[key] = err.response.data.errors[key][0];
        });
        setErrors(serverErrors);
      } else {
        setError(err.response?.data?.message || 'Erro ao redefinir senha. O link pode estar expirado.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForgot = () => {
    navigate(userType === 'associate' ? '/associado/forgot-password' : '/forgot-password');
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-primary-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="mx-auto h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Senha Redefinida com Sucesso!
            </h2>
            <p className="text-gray-600 mb-6">
              Sua senha foi alterada com sucesso.
            </p>
            <p className="text-sm text-gray-500">
              Redirecionando para o login...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-primary-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-16 bg-gradient-to-r from-primary-500 to-primary-700 rounded-xl flex items-center justify-center mb-4 shadow-lg">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              Redefinir Senha
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Digite sua nova senha
            </p>
          </div>

          {error && (
            <Alert variant="error" onClose={() => setError('')} className="mb-6">
              {error}
              {error.includes('expirado') && (
                <button
                  onClick={handleBackToForgot}
                  className="mt-2 text-sm underline hover:no-underline"
                >
                  Solicitar novo link
                </button>
              )}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Nova Senha"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) {
                  setErrors((prev) => ({ ...prev, password: '' }));
                }
              }}
              error={errors.password}
              placeholder="Mínimo 6 caracteres"
              required
              disabled={loading || !token}
              autoFocus
            />

            <Input
              label="Confirmar Nova Senha"
              type="password"
              value={passwordConfirmation}
              onChange={(e) => {
                setPasswordConfirmation(e.target.value);
                if (errors.passwordConfirmation) {
                  setErrors((prev) => ({ ...prev, passwordConfirmation: '' }));
                }
              }}
              error={errors.passwordConfirmation}
              required
              disabled={loading || !token}
            />

            <Button
              type="submit"
              className="w-full"
              loading={loading}
              disabled={loading || !token}
            >
              {loading ? 'Redefinindo...' : 'Redefinir Senha'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleBackToForgot}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Solicitar novo link de recuperação
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
